import React, { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Toaster } from './components/ui/sonner';
import CodexAlarmWatcher from './components/CodexAlarmWatcher';
import { initAdMob } from './config/admob';
import { useAndroidBackButton } from './hooks/useAndroidBackButton';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import AdminPanel from './pages/AdminPanel';
import Home from './pages/Home';
import Discover from './pages/Discover';
import AuthCallback from './pages/AuthCallback';
import UserProfile from './pages/UserProfile';
import LandingPage from './pages/LandingPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Features from './pages/Features';
import SharedBook from './pages/SharedBook';
import SharedMuseumRoom from './pages/SharedMuseumRoom';
import { BlissLoader } from './components/BlissLoader';

// Root route: Landing Page for visitors, Home for logged-in users
function RootRoute() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <BlissLoader />
    );
  }

  return user ? <Home /> : <LandingPage />;
}

// Redirect logged-in users away from auth pages
function AuthRedirect({ children }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!loading && user) {
      navigate('/home', { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <BlissLoader />
    );
  }

  if (user) return null;
  return children;
}

function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* ROOT: Landing Page (visitors) or Home (logged-in) */}
      <Route path="/" element={<RootRoute />} />

      {/* Explicit /home route for logged-in users */}
      <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />

      {/* Old purple intro page preserved at /app-intro */}
      <Route path="/app-intro" element={<AuthRedirect><Login /></AuthRedirect>} />

      {/* Auth pages */}
      <Route path="/login" element={<AuthRedirect><Login /></AuthRedirect>} />
      <Route path="/register" element={<AuthRedirect><Register /></AuthRedirect>} />
      <Route path="/forgot-password" element={<AuthRedirect><ForgotPassword /></AuthRedirect>} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/features" element={<Features />} />
      {/* Public landing page — always shown regardless of auth state.
          Used by shared pages so a visitor viewing a share is returned here,
          not silently redirected into their own dashboard. */}
      <Route path="/welcome" element={<LandingPage />} />

      {/* PUBLIC SHARE ROUTE — anyone with the link can open the book
          without logging in. Reads from the public /api/share/book endpoint. */}
      <Route path="/share/book/:mode/:userId" element={<SharedBook />} />
      <Route path="/share/museum/:roomId/:userId" element={<SharedMuseumRoom />} />

      {/* Protected pages */}
      <Route path="/discover" element={<ProtectedRoute><Discover /></ProtectedRoute>} />
      <Route path="/user/:email" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Initialize AdMob SDK on app boot — no-ops on web, activates on native (Capacitor).
  useEffect(() => { initAdMob(); }, []);
  // Wire Android hardware/gesture back to walk the history stack instead
  // of exiting the APK. No-op on plain web.
  useAndroidBackButton();

  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster />
        <CodexAlarmWatcher />
      </AuthProvider>
    </div>
  );
}

export default App;
